import { colors as themeColors } from '../../../utils/theme';
import { useState, useEffect, useRef } from 'react';
import { TextField, Paper, Table, TableHead, TableRow, TableCell, TableBody, TableFooter, Typography, IconButton, Dialog, DialogContent, Box, Fade, useTheme, useMediaQuery, Card, CardContent, Divider, Grid} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ProductMaster from '../../ProductMaster';

const DropdownProductCode = ({ id, options, value, onSelect, onNewProductAdded }) => {
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up('md')); // ✅ Detect md and above

    const [inputValue, setInputValue] = useState(value || '');
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState([]);
    const [showPopup, setShowPopup] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => { setInputValue(value || ''); }, [value]);

    useEffect(() => {
        if (inputValue) {
            const filtered = options.filter(opt => opt.itemCode.toLowerCase().startsWith(inputValue.toLowerCase()) );
            setFilteredOptions(filtered);
        } else {
            setFilteredOptions([]);
        }
    }, [inputValue, options]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (e) => {
        setInputValue(e.target.value);
        setDropdownOpen(true);
    };

    const handleSelect = (option) => {
        setInputValue(option.itemCode);
        onSelect(option);
        setDropdownOpen(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && filteredOptions.length) {
            handleSelect(filteredOptions[0]);
        }
    };

    const handleClosePopup = () => {
        // 1) close the dialog
        setShowPopup(false);
        // 2) ask parent to re-fetch its master list
        onNewProductAdded?.();      // ← trigger parent reload
        // 3) if the user still has text in the box, re-open the dropdown
        if (inputValue) setDropdownOpen(true);
    };

    return (
            <Box sx={{ position: 'relative' }} ref={dropdownRef}>
                <TextField fullWidth label="Product Code" id={id} variant="outlined" value={inputValue} size="small" onChange={handleInputChange} onFocus={() => setDropdownOpen(true)} onKeyDown={handleKeyDown} />
    
                <Fade in={dropdownOpen && Boolean(inputValue) && !showPopup}>
                    <Paper elevation={3} sx={{ position: 'absolute', top: `calc(100% + ${theme.spacing(1)})`, left: 0, width: '100%', minWidth: isMdUp ? 800 : 'auto', maxWidth: '100vw', maxHeight: 400, overflowY: 'auto', borderRadius: 2, boxShadow: theme.shadows[4], zIndex: theme.zIndex.tooltip, mt: 1, px: 1, boxSizing: 'border-box' }} >
    
                        {isMdUp ? (
                            // ✅ TABLE VIEW FOR MEDIUM & UP
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: themeColors.neutral.border }}>
                                        {['Item Code', 'Description', 'Unit', 'GST%', 'Rate', 'Discount%'].map((h, i) => (
                                            <TableCell key={i}><Typography fontWeight="bold">{h}</Typography></TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredOptions.length > 0 ? (
                                        filteredOptions.map((opt, idx) => (
                                            <TableRow hover key={idx} onClick={() => handleSelect(opt)} sx={{ cursor: 'pointer' }} >
                                                <TableCell>{opt.itemCode}</TableCell>
                                                <TableCell>{opt.description}</TableCell>
                                                <TableCell>{opt.Unit}</TableCell>
                                                <TableCell>{opt.GST}</TableCell>
                                                <TableCell>{opt.saleRate}</TableCell>
                                                <TableCell>{opt.SaleDisc}</TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
                                                <Typography color="text.secondary">No results found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                                <TableFooter>
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 1 }}>
                                            <IconButton color="primary" onClick={() => setShowPopup(true)}>
                                                <AddCircleOutlineIcon /> Add New Product
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                </TableFooter>
                            </Table>
                        ) : (
                            // ✅ CARD VIEW FOR SMALL SCREENS
                            filteredOptions.length > 0 ? (
                                filteredOptions.map((opt, idx) => (
                                    <Card key={idx} variant="outlined"
                                        sx={{ mb: 1, cursor: 'pointer', '&:hover': { backgroundColor: themeColors.neutral.border } }}
                                        onClick={() => handleSelect(opt)}
                                    >
                                        <CardContent sx={{ py: 1.5 }}>
                                            <Typography variant="subtitle1" fontWeight="bold">{opt.itemCode}</Typography>
                                            <Typography variant="body2" color="text.secondary">{opt.description}</Typography>
                                            <Divider sx={{ my: 1 }} />
                                            <Grid container spacing={1}>
                                                <Grid item xs={6}><strong>Unit:</strong> {opt.unit}</Grid>
                                                <Grid item xs={6}><strong>GST%:</strong> {opt.GST}</Grid>
                                                <Grid item xs={6}><strong>Rate:</strong> ₹{opt.saleRate}</Grid>
                                                <Grid item xs={6}><strong>Disc%:</strong> {opt.saleDiscount}</Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                ))
                            ) : (
                                <Typography textAlign="center" sx={{ py: 2 }} color="text.secondary">No results found</Typography>
                            )
                        )}
                    </Paper>
                </Fade>
    
                <Dialog fullWidth maxWidth="lg" open={showPopup}
                    onClose={handleClosePopup}      // ← now uses the unified handler
                >
                    <DialogContent sx={{ p: 0 }}>
                        <ProductMaster 
                            mode="add" 
                            onPopupCancel={handleClosePopup}  // ← same handler when the child taps “Cancel” or “Save”
                        />
                    </DialogContent>
                </Dialog>
            </Box>
        );
};

export default DropdownProductCode;